/* eslint-disable camelcase */
import { useEffect, useMemo, useRef, useState } from 'react';
import { itIT } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import Avatar from '../../components/User/Avatar';
import CustomChip from '../../components/Chips/CustomChip';
import CheckSuccessIcon from '../../components/Icons/CheckSuccessIcon';
import CheckFailIcon from '../../components/Icons/CheckFailIcon';
import { StripedDataGrid } from '../../components/DataGrid/StripedDataGrid';

const SelectAllCheckbox = ({
  rows,
  localSelectedRows,
  setLocalSelectedRows,
}) => {
  const checkboxRef = useRef(null);
  const isIndeterminate =
    localSelectedRows.length > 0 && localSelectedRows.length < rows.length;
  const isChecked = rows.length > 0 && localSelectedRows.length === rows.length;

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  return (
    <input
      ref={checkboxRef}
      type="checkbox"
      onChange={(e) => {
        const allIds = rows.map((row) => row.id);
        setLocalSelectedRows(e.target.checked ? allIds : []);
      }}
      checked={isChecked}
    />
  );
};

SelectAllCheckbox.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  localSelectedRows: PropTypes.arrayOf(PropTypes.string).isRequired,
  setLocalSelectedRows: PropTypes.func.isRequired,
};

const AdministrationDataGrid = ({ data, generatePDF, setSelectedRows }) => {
  const [localSelectedRows, setLocalSelectedRows] = useState([]);

  const rows = useMemo(
    () =>
      data.map((row) => ({
        ...row,
        id: row.username || row.id || row.full_name,
      })),
    [data]
  );

  useEffect(() => {
    if (setSelectedRows) {
      const selectedRowsData = localSelectedRows
        .map((id) => rows.find((row) => row.id === id))
        .filter(Boolean);

      const rowsWithOriginal = selectedRowsData.map((row) => ({
        ...row,
        original: row,
      }));

      setSelectedRows(rowsWithOriginal);
    }
  }, [localSelectedRows, rows, setSelectedRows]);

  const columns = useMemo(
    () => [
      {
        field: 'selection',
        headerName: '',
        width: 50,
        sortable: false,
        renderHeader: () => (
          <SelectAllCheckbox
            rows={rows}
            localSelectedRows={localSelectedRows}
            setLocalSelectedRows={setLocalSelectedRows}
          />
        ),
        renderCell: (params) => (
          <input
            type="checkbox"
            checked={localSelectedRows.includes(params.row.id)}
            onChange={(e) => {
              if (e.target.checked) {
                setLocalSelectedRows((prev) => [...prev, params.row.id]);
              } else {
                setLocalSelectedRows((prev) =>
                  prev.filter((id) => id !== params.row.id)
                );
              }
            }}
          />
        ),
      },
      {
        field: 'full_name',
        headerName: 'Utente',
        flex: 2,
        renderCell: ({ value, row }) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <Avatar username={row.username} />
            <span>{value}</span>
          </div>
        ),
      },
      {
        field: 'all_confirmed',
        headerName: 'Stato del mese',
        flex: 2,
        renderCell: ({ value }) => (
          <CustomChip
            value={value ? 'Chiuso' : 'Aperto'}
            bgColor={value ? 'rgba(75, 181, 67, 0.27)' : '#ffd4d4'}
            textColor={value ? '#246d1f' : '#ff0000'}
            isIconify={false}
            IconComponent={value ? CheckFailIcon : CheckSuccessIcon}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'PDF',
        flex: 1,
        renderCell: (params) => {
          const { all_confirmed, username, last_name, first_name } = params.row;

          const handleClick = (e) => {
            e.preventDefault();
            if (all_confirmed) {
              generatePDF(
                [username],
                ['worklogs', 'expenses', 'trips'],
                `${last_name} ${first_name} (${username})`
              );
            }
          };

          return (
            <a
              href="#"
              onClick={handleClick}
              style={{
                color: all_confirmed ? '#0a58ca' : '#ccc',
                textDecoration: all_confirmed ? 'underline' : 'none',
                cursor: all_confirmed ? 'pointer' : 'default',
                pointerEvents: all_confirmed ? 'auto' : 'none',
              }}
            >
              Scarica
            </a>
          );
        },
      },
    ],
    [rows, localSelectedRows, generatePDF]
  );
  return (
    <StripedDataGrid
      localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
      rows={rows}
      columns={columns}
      density="standard"
      disableColumnMenu
      disableRowSelectionOnClick
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
      }
      getRowHeight={() => 'auto'}
      initialState={{
        sorting: {
          sortModel: [
            {
              field: 'all_confirmed',
              sort: 'asc',
            },
          ],
        },
        pagination: {
          paginationModel: {
            pageSize: 100,
          },
        },
      }}
      pageSizeOptions={[25, 50, 100]}
      sx={{
        height: '100%',
        minHeight: 400,
        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
        '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
        '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
          py: '22px',
        },
      }}
    />
  );
};

AdministrationDataGrid.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  generatePDF: PropTypes.func.isRequired,
  setSelectedRows: PropTypes.func,
};

AdministrationDataGrid.defaultProps = {
  setSelectedRows: () => {},
};

export default AdministrationDataGrid;
